import React from 'react';
import { inject, observer, Provider } from 'mobx-react';
import FontAwesome from 'react-fontawesome';
import { makeObservable, observable, reaction, action, runInAction } from 'mobx';
import { Button, Col, Row } from 'antd';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import { Switch } from 'antd';
import SankeyTransition from './Transitions/SankeyTransition/SankeyTransition';
import GroupTimepoint from './Timepoints/GroupTimepoint';
import TimepointLabels from './PlotLabeling/TimepointLabels';
import RowOperators from './RowOperatorsNew';
import Legend from './Legend';
import CustomGrouping from './StateTransition/CustomGrouping';
import { withUICallbacks } from './UICallbacksContext';

/**
 * Component for the Block view
 */
const BlockView = inject(
	'rootStore',
	'uiStore',
	'undoRedoStore'
)(
	observer(
		class BlockView extends React.Component {
			highlightedVariable = '';
			order = ['labels', 'operators', 'view', 'legend'];
			width = window.innerWidth;
			height = window.innerHeight - 250;
			hasBackground = true;
			panes;
			ref = React.createRef();
			active = {
				labels: false,
				operators: false,
				view: false,
				legend: false,
			};

			constructor(props) {
				super(props);
				this.padding = 20;
				this.blockWidthRatio = 0.75;
				this.blockView = React.createRef();

				this.panes = {
					labels: { width: (((window.innerWidth - 40) * this.blockWidthRatio) / 10) * 0.7, active: false },
					operators: { width: (((window.innerWidth - 40) * this.blockWidthRatio) / 10) * 1.3, active: false },
					view: { width: (((window.innerWidth - 40) * this.blockWidthRatio) / 10) * 6.5, active: false },
					legend: { width: (((window.innerWidth - 40) * this.blockWidthRatio) / 10) * 1.3, active: false },
				};

				makeObservable(this, {
					highlightedVariable: observable,
					order: observable,
					width: observable,
					height: observable,
					hasBackground: observable,
					panes: observable,
					active: observable,
					updateDimensions: action,
					updatePaneWidth: action,
					setHighlightedVariable: action,
					removeHighlightedVariable: action,
					handleTimeClick: action,
				});

				this.handleTimeClick = this.handleTimeClick.bind(this);
				this.setHighlightedVariable = this.setHighlightedVariable.bind(this);
				this.removeHighlightedVariable = this.removeHighlightedVariable.bind(this);
				this.updateDimensions = this.updateDimensions.bind(this);
				this.updatePaneWidth = this.updatePaneWidth.bind(this);

				reaction(
					() => this.panes.view.width,
					(width) => {
						this.props.rootStore.visStore.setPlotWidth(width - 10);
					}
				);
			}

			/**
			 * Add event listener
			 */
			componentDidMount() {
				this.width = this.ref.current.getBoundingClientRect().width;
				this.updateDimensions();

				this.props.rootStore.visStore.setPlotWidth(this.panes.view.width - 10);
				this.props.rootStore.visStore.setPlotHeight(
					window.innerHeight - this.blockView.current.getBoundingClientRect().top
				);
				window.addEventListener('resize', this.updateDimensions);
			}

			/**
			 * Remove event listener
			 */
			componentWillUnmount() {
				window.removeEventListener('resize', this.updateDimensions);
			}

			/**
			 * updates view dimensions
			 */
			updateDimensions() {
				const prevWidth = Object.values(this.panes)
					.map((d) => d.width)
					.reduce((a, b) => a + b);
				this.panes = {
					labels: {
						width: ((this.width - 40) * this.blockWidthRatio) / (prevWidth / this.panes.labels.width),
					},
					operators: {
						width: ((this.width - 40) * this.blockWidthRatio) / (prevWidth / this.panes.operators.width),
					},
					view: {
						width: ((this.width - 40) * this.blockWidthRatio) / (prevWidth / this.panes.view.width),
					},
					legend: {
						width: ((this.width - 40) * this.blockWidthRatio) / (prevWidth / this.panes.legend.width),
					},
				};
				this.props.rootStore.visStore.setPlotHeight(
					window.innerHeight - this.blockView.current.getBoundingClientRect().top
				);

				this.height = window.innerHeight - 250;
			}

			/**
			 * updates a single pane width
			 * @param {string} paneName
			 * @param {number} width
			 */
			updatePaneWidth(paneName, width) {
				this.panes[paneName].width = width;
			}

			/**
			 * sets a variable to be highlighted
			 * @param {string} newHighlighted
			 */
			setHighlightedVariable(newHighlighted) {
				this.highlightedVariable = newHighlighted;
			}

			/**
			 * removes the highlighted variable
			 */
			removeHighlightedVariable() {
				this.highlightedVariable = '';
			}

			/**
			 * handle visualizing real time
			 */
			handleTimeClick() {
				this.props.rootStore.dataStore.applyPatientOrderToAll(0);
				this.props.uiStore.setRealTime(!this.props.uiStore.realTime);
				this.props.undoRedoStore.saveRealTimeHistory(this.props.uiStore.realTime);
			}

			/**
			 * gets timepoints and transitions
			 * @return {*[]}
			 */
			getTimepointAndTransitions() {
				const timepoints = [];
				const transitions = [];
				this.props.rootStore.dataStore.timepoints.forEach((d, i) => {
					const rectWidth = this.props.rootStore.visStore.sampleRectWidth;
					// check the type of the timepoint to get the correct width of the heatmap rectangles

					// create timepoints
					if (d.heatmap) {
						const transformTP = `translate(
                        ${this.props.rootStore.visStore.getTpXTransform(i)},
                        ${this.props.rootStore.visStore.newTimepointPositions.timepoint[i]}
                        )`;
						timepoints.push(
							<g key={d.globalIndex} transform={transformTP}>
								<Provider
									dataStore={this.props.rootStore.dataStore}
									visStore={this.props.rootStore.visStore}
								>
									<GroupTimepoint
										type={d.type}
										group={d.customPartitions.length > 0 ? d.customGrouped : d.grouped}
										heatmap={d.heatmap}
										index={i}
										currentVariables={
											this.props.rootStore.dataStore.variableStores[d.type].fullCurrentVariables
										}
										rectWidth={rectWidth}
										tooltipFunctions={this.props.tooltipFunctions}
										primaryVariableId={d.primaryVariableId}
										hasBackground={this.hasBackground}
									/>
								</Provider>
							</g>
						);
					}
					// create transitions
					// if(d.type=='between') return
					if (i !== this.props.rootStore.dataStore.timepoints.length - 1) {
						const transformTR = `translate(0,${this.props.rootStore.visStore.newTimepointPositions.connection[i]})`;
						const firstTP = d;
						const secondTP = this.props.rootStore.dataStore.timepoints[i + 1];
						// if (secondTP.type=='between' & i<this.props.rootStore.dataStore.timepoints.length - 2){
						//     secondTP = this.props.rootStore.dataStore.timepoints[i + 2];
						// }
						// All timepoints are always displayed as GroupTimepoint in BlockViewNew,
						// so transitions should always use SankeyTransition (grouped-to-grouped).
						const firstGrouped = firstTP.customPartitions.length > 0 ? firstTP.customGrouped : firstTP.grouped;
						const secondGrouped = secondTP.customPartitions.length > 0 ? secondTP.customGrouped : secondTP.grouped;
						const transition = (
							<Provider
								dataStore={this.props.rootStore.dataStore}
								visStore={this.props.rootStore.visStore}
							>
								<SankeyTransition
									index={i}
									firstGrouped={firstGrouped}
									secondGrouped={secondGrouped}
									firstPrimary={this.props.rootStore.dataStore.variableStores[
										firstTP.type
									].getById(firstTP.primaryVariableId)}
									secondPrimary={this.props.rootStore.dataStore.variableStores[
										secondTP.type
									].getById(secondTP.primaryVariableId)}
									tooltipFunctions={this.props.tooltipFunctions}
								/>
							</Provider>
						);
						transitions.push(
							<g key={firstTP.globalIndex} transform={transformTR}>
								{transition}
							</g>
						);
					}
				});
				return [transitions, timepoints];
			}

			render() {
				return (
					<div className="blockView" ref={this.ref}>
						<div className="view" id="block-view" style={{ height: this.height, overflowY: 'scroll' }}>
							<Row style={{ marginLeft: '0' }}>
								<Button
									size="sm"
									onClick={this.handleTimeClick}
									disabled={
										this.props.uiStore.selectedTab === 'line' ||
										this.props.rootStore.dataStore.variableStores.between.currentVariables.length >
											0
									}
									key="actualTimeline"
								>
									<FontAwesome name="clock" />{' '}
									{this.props.uiStore.realTime ? 'Hide Relative Time' : 'Show Relative Time'}
								</Button>
							</Row>
							<Row>
								<Col span={6}>
									<CustomGrouping />
								</Col>
								<Col span={18}>
									<PanelGroup direction="horizontal">
										<Panel
											defaultSize={7}
											minSize={5}
											className="pane-inactive timepointLabel"
											onResize={(size) => {
												const containerWidth =
													this.ref.current?.getBoundingClientRect().width || this.width;
												const actualWidth =
													(containerWidth - 40) * this.blockWidthRatio * (size / 100);
												this.updatePaneWidth('labels', actualWidth);
											}}
										>
											<Provider
												dataStore={this.props.rootStore.dataStore}
												visStore={this.props.rootStore.visStore}
												uiStore={this.props.uiStore}
											>
												<TimepointLabels
													width={Math.max(50, this.panes.labels.width - 10)}
													padding={this.padding}
												/>
											</Provider>
										</Panel>

										<PanelResizeHandle className="resize-handle" />

										<Panel
											defaultSize={13}
											minSize={10}
											className="pane-inactive variableOperator"
											style={{ paddingTop: this.padding }}
											onResize={(size) => {
												const containerWidth =
													this.ref.current?.getBoundingClientRect().width || this.width;
												const actualWidth =
													(containerWidth - 40) * this.blockWidthRatio * (size / 100);
												this.updatePaneWidth('operators', actualWidth);
											}}
										>
											<RowOperators
												highlightedVariable={this.highlightedVariable}
												width={this.panes.operators.width - 10}
												setHighlightedVariable={this.setHighlightedVariable}
												removeHighlightedVariable={this.removeHighlightedVariable}
											/>
										</Panel>

										<PanelResizeHandle className="resize-handle" />

										<Panel
											defaultSize={65}
											minSize={30}
											className="pane-inactive"
											style={{ paddingTop: this.padding }}
											onResize={(size) => {
												const containerWidth =
													this.ref.current?.getBoundingClientRect().width || this.width;
												const actualWidth =
													(containerWidth - 40) * this.blockWidthRatio * (size / 100);
												this.updatePaneWidth('view', actualWidth);
											}}
										>
											<div ref={this.blockView} className="scrollableX">
												<Switch
													size="small"
													onChange={() => runInAction(() => (this.hasBackground = !this.hasBackground))}
													checkedChildren="bg"
													unCheckedChildren="bg"
													style={{ position: 'absolute', top: '-15px' }}
												/>
												<svg
													width={this.props.rootStore.visStore.svgWidth}
													height={this.props.rootStore.visStore.svgHeight}
												>
													{' '}
													<g transform={`translate(5, 5)`}>
														{this.getTimepointAndTransitions()}
													</g>
												</svg>
											</div>
										</Panel>

										<PanelResizeHandle className="resize-handle" />

										<Panel
											defaultSize={13}
											minSize={10}
											className="pane-inactive"
											style={{ paddingTop: this.padding }}
											onResize={(size) => {
												const containerWidth =
													this.ref.current?.getBoundingClientRect().width || this.width;
												const actualWidth =
													(containerWidth - 40) * this.blockWidthRatio * (size / 100);
												this.updatePaneWidth('legend', actualWidth);
											}}
										>
											<Legend
												highlightedVariable={this.highlightedVariable}
												setHighlightedVariable={this.setHighlightedVariable}
												removeHighlightedVariable={this.removeHighlightedVariable}
												{...this.props.tooltipFunctions}
											/>
										</Panel>
									</PanelGroup>
								</Col>
							</Row>
						</div>
						<form id="svgform" method="post">
							<input type="hidden" id="output_format" name="output_format" value="" />
							<input type="hidden" id="data" name="data" value="" />
						</form>
					</div>
				);
			}
		}
	)
);
export default withUICallbacks(BlockView);
